import { React, useCallback, useEffect, useState } from "react";
import PlantillaScreen from "../helpers/PlantillaScreen";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { LoaderScreen } from "../components/LoaderScreen";
import Swal from "sweetalert2";
import request from "../../Helpers/API";
import EditarPresentacionModal from "../PermisosUsuarios/Presentaciones/EditarPresentacionModal";

const TITULO = "PLANEACIÓN ESTRATÉGICA";

export const PlaneacionEstrategicaScreen = () => {
    const [presentaciones, setPresentaciones] = useState([]);
    const [openModalEditar, setOpenModalEditar] = useState(false);
    const [selectedPresentacion, setSelectedPresentacion] = useState(null);
    const [loader, setLoader] = useState({
        mostrar: false,
        alerta: false,
        error: false,
        mensaje: "",
    });

    const cargarPresentaciones = useCallback(() => {
        setLoader((prev) => ({ ...prev, mostrar: true }));
        request
            .get("Presentacion/Listado")
            .then((result) => {
                setPresentaciones(Array.isArray(result) ? result : []);
                setLoader((prev) => ({ ...prev, mostrar: false }));
            })
            .catch(() => {
                setLoader((prev) => ({ ...prev, mostrar: false }));
                Swal.fire(
                    TITULO,
                    "No fue posible cargar las presentaciones. Intenta de nuevo más tarde.",
                    "error"
                );
            });
    }, []);

    // Al cerrarse el modal de edición se refresca el listado.
    useEffect(() => {
        if (!openModalEditar) cargarPresentaciones();
    }, [openModalEditar, cargarPresentaciones]);

    const handleVerPresentacion = (htmlContenido) => {
        const blob = new Blob([htmlContenido], { type: "text/html" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
    };

    const handleOpenModalEditar = (event, item) => {
        event.stopPropagation();
        setSelectedPresentacion(item);
        setOpenModalEditar(true);
    };

    const handleCloseModalEditar = () => {
        setSelectedPresentacion(null);
        setOpenModalEditar(false);
    };

    const handleEliminar = (event, item) => {
        event.stopPropagation();

        Swal.fire({
            title: TITULO,
            html: `¿Deseas eliminar la presentación <b>${item.nombre}</b>? Esta acción no se puede deshacer.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#e24b4a",
            cancelButtonColor: "#8d9688",
            reverseButtons: true,
        }).then((resultado) => {
            if (!resultado.isConfirmed) return;

            setLoader((prev) => ({ ...prev, mostrar: true }));
            request
                .get(
                    `Presentacion/Eliminar?presentacionId=${item.presentacionId}`
                )
                .then(() => {
                    setLoader((prev) => ({ ...prev, mostrar: false }));
                    Swal.fire(
                        TITULO,
                        "Presentación eliminada con éxito",
                        "success"
                    );
                    cargarPresentaciones();
                })
                .catch(() => {
                    setLoader((prev) => ({ ...prev, mostrar: false }));
                    Swal.fire(
                        TITULO,
                        "No fue posible eliminar la presentación. Intenta de nuevo más tarde.",
                        "error"
                    );
                });
        });
    };

    return (
        <PlantillaScreen
            titulo={TITULO}
            className="planeacion-main-screen"
            className_main={1}
        >
            <div className="planeacion-container">
                <LoaderScreen loader={loader} />

                <div className="planeacion-header">
                    <div className="planeacion-header-title">
                        <FormatListNumberedIcon />
                        Presentaciones Disponibles
                    </div>
                    <div className="planeacion-header-contador">
                        {presentaciones.length}{" "}
                        {presentaciones.length === 1 ? "presentación" : "presentaciones"}
                    </div>
                </div>

                {!loader.mostrar && presentaciones.length === 0 && (
                    <div className="planeacion-empty">
                        <SlideshowIcon />
                        <span>No hay presentaciones disponibles</span>
                    </div>
                )}

                <div className="planeacion-cards-grid">
                    {presentaciones.map((item) => {
                        const tieneContenido =
                            item.htmlContenido && item.htmlContenido.trim() !== "";
                        return (
                            <div
                                key={item.presentacionId}
                                className={`planeacion-card ${tieneContenido
                                        ? "planeacion-card--disponible"
                                        : "planeacion-card--sin-contenido"
                                    }`}
                                onClick={() =>
                                    tieneContenido &&
                                    handleVerPresentacion(item.htmlContenido)
                                }
                            >
                                <div className="planeacion-card-icon">
                                    <SlideshowIcon />
                                </div>
                                <div className="planeacion-card-body">
                                    <div className="planeacion-card-nombre-row">
                                        <div className="planeacion-card-nombre">
                                            {item.nombre}
                                        </div>
                                        <div className="planeacion-card-acciones">
                                            <button
                                                type="button"
                                                className="planeacion-card-btn-accion planeacion-card-btn-accion--editar"
                                                title="Editar presentación"
                                                aria-label={`Editar ${item.nombre}`}
                                                onClick={(event) =>
                                                    handleOpenModalEditar(event, item)
                                                }
                                            >
                                                <EditIcon />
                                            </button>
                                            <button
                                                type="button"
                                                className="planeacion-card-btn-accion planeacion-card-btn-accion--eliminar"
                                                title="Eliminar presentación"
                                                aria-label={`Eliminar ${item.nombre}`}
                                                onClick={(event) =>
                                                    handleEliminar(event, item)
                                                }
                                            >
                                                <DeleteOutlineIcon />
                                            </button>
                                        </div>
                                    </div>
                                    {item.descripcion && (
                                        <div className="planeacion-card-descripcion">
                                            {item.descripcion}
                                        </div>
                                    )}
                                </div>
                                <div className="planeacion-card-footer">
                                    {tieneContenido ? (
                                        <div className="planeacion-card-btn-ver">
                                            <VisibilityIcon />
                                            Ver Presentación
                                        </div>
                                    ) : (
                                        <div className="planeacion-card-sin-contenido">
                                            Sin contenido cargado
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <EditarPresentacionModal
                    open={openModalEditar}
                    onClose={handleCloseModalEditar}
                    presentacion={selectedPresentacion}
                />
            </div>
        </PlantillaScreen>
    );
};
